/**
 * Revenue matrix: one company's revenue as products (rows) × cycles (columns)
 * (`buildRevenueMatrix`), or one product's revenue as companies × cycles
 * (`buildProductCompanyMatrix`). Both run on the generic `buildRevenueMatrixBy`
 * core, so the rules below apply to every row family.
 *
 * Pure, no React. Callers hand in lines that are ALREADY in one display
 * currency; this module never converts and never sums across currencies.
 *
 * ⚠️ **Money is exact.** Every amount is parsed into scaled `BigInt` units at
 * the widest scale any line carries (at least 2), summed exactly, and rendered
 * back at that scale. Never `Number()`/`parseFloat` here. Σ row totals =
 * Σ column totals = grandTotal = Σ line amounts, and `assertReconciles` throws
 * in development when that is not true.
 *
 * ⚠️ **`null` is not 0.** A cell with no line is `null`; a cell whose lines net
 * to zero is `"0.00"`. The same holds for column totals and YoY.
 *
 * Grouping (owner decision): catalogued lines group by product id; uncatalogued
 * lines group by Xero item code when a line carries one, else by
 * `normaliseDescription(lineDescription)`; lines with no description share one
 * row. Key prefixes keep the families from colliding.
 *
 * Columns (owner decision): every cycle from the earliest cycle with revenue
 * through the current cycle (or the latest cycle with revenue), in START-DATE
 * order — never id order, cycle ids are non-contiguous. A cycle that has lines
 * is never dropped, even when it falls outside that range.
 */

export type MatrixCycle = { id: string; name: string; start: string /* YYYY-MM-DD */ };

export type MatrixLine = {
  cycleId: string;
  productId: string | null;
  productName: string | null;
  isUncatalogued: boolean;
  lineDescription: string | null;
  /** decimal string, already in the display currency */
  amount: string;
  invoiceNumber: string | null;
  invoiceId: string | null;
  /**
   * Optional and not yet on the wire: the Xero item code of an uncatalogued
   * line. When present and non-blank it is the uncatalogued grouping key.
   */
  xeroItemCode?: string | null;
};

export type MatrixRow = {
  key: string;
  label: string;
  /** Secondary label (e.g. a company's billing region). Absent on product rows; null when none is known. */
  sublabel?: string | null;
  isUncatalogued: boolean;
  cells: (string | null)[];
  total: string;
  /** percent, 1dp, e.g. "12.3"; null when the grand total is zero or negative (no meaningful share) */
  share: string | null;
};

export type RevenueMatrix = {
  /** columns, oldest → newest */
  cycles: MatrixCycle[];
  /** default order: total desc, then label asc */
  rows: MatrixRow[];
  /** null when no line in that cycle */
  columnTotals: (string | null)[];
  grandTotal: string;
  /** % change vs previous column total, 1dp signed; null for the first column or when either side is null, or previous is zero */
  yoy: (string | null)[];
  /** largest cell, for heat shading */
  maxCell: string | null;
};

export type MatrixSort = { by: 'label' | 'total' | { cycleIndex: number }; dir: 'asc' | 'desc' };

/**
 * A row once the reader has hidden some cycles (`projectMatrix`). Its total is
 * the sum of the VISIBLE cells only, so it can be absent: a row the reader kept
 * whose visible cells are all empty has no total, never `"0.00"`.
 */
export type VisibleMatrixRow = Omit<MatrixRow, 'total' | 'share'> & {
  total: string | null;
  /** Share of the VISIBLE grand total, 1dp; null whenever either side is absent or the grand total is not positive. */
  share: string | null;
};

/**
 * What the matrix table renders and exports: only the visible rows × visible
 * cycles, every total, share, YoY and the heat scale recomputed over them.
 * A whole `RevenueMatrix` is one of these (nothing hidden).
 */
export type VisibleMatrix = {
  cycles: MatrixCycle[];
  rows: VisibleMatrixRow[];
  columnTotals: (string | null)[];
  /** null when no visible cell holds a line. */
  grandTotal: string | null;
  yoy: (string | null)[];
  maxCell: string | null;
};

/**
 * The row key of a catalogued product — the one spelling, shared by the
 * grouping below and by anything that needs to name a product row from
 * outside the matrix (the "Hide add-ons" switch). ⛔ Never rename: the key is
 * also the hidden id stored in the reader's preferences.
 */
export function productRowKey(productId: string): string {
  return `product:${productId}`;
}

const UNCATALOGUED_PREFIX = 'uncatalogued:';
const NO_DESCRIPTION_KEY = 'uncatalogued:none';
const NO_DESCRIPTION_LABEL = 'Imported from Xero (no description)';
const NO_LINES_KEY = 'invoice:no-lines';
const NO_LINES_LABEL = 'Invoice total (no line detail)';
const MIN_SCALE = 2;

// ---------------------------------------------------------------------------
// Exact decimal helpers (private; lib/decimal.ts keeps its own private).
// ---------------------------------------------------------------------------

type Scaled = { units: bigint; scale: number };

const DECIMAL_PATTERN = /^-?\d+(\.\d+)?$/;

function parseDecimal(value: string): Scaled | null {
  const trimmed = value.trim();
  if (!DECIMAL_PATTERN.test(trimmed)) return null;
  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [whole = '0', fraction = ''] = unsigned.split('.');
  const units = BigInt(whole + fraction);
  return { units: negative ? -units : units, scale: fraction.length };
}

function rescale(value: Scaled, scale: number): bigint {
  return value.units * 10n ** BigInt(scale - value.scale);
}

function formatUnits(units: bigint, scale: number): string {
  const negative = units < 0n;
  const digits = (negative ? -units : units).toString().padStart(scale + 1, '0');
  const body =
    scale === 0
      ? digits
      : `${digits.slice(0, digits.length - scale)}.${digits.slice(digits.length - scale)}`;
  return negative ? `-${body}` : body;
}

/** Integer division rounding half away from zero. */
function divideRoundHalf(numerator: bigint, denominator: bigint): bigint {
  const negative = numerator < 0n !== denominator < 0n;
  const a = numerator < 0n ? -numerator : numerator;
  const b = denominator < 0n ? -denominator : denominator;
  const quotient = (a * 2n + b) / (b * 2n);
  return negative ? -quotient : quotient;
}

/** Renders tenths (e.g. 123n → "12.3"); zero is always "0.0", never "-0.0". */
function formatTenths(tenths: bigint, signed: boolean): string {
  if (tenths === 0n) return '0.0';
  const negative = tenths < 0n;
  const abs = negative ? -tenths : tenths;
  const body = `${abs / 10n}.${abs % 10n}`;
  if (negative) return `-${body}`;
  return signed ? `+${body}` : body;
}

function isDevBuild(): boolean {
  // Only an explicit DEV=true throws. Production (and any non-Vite runtime)
  // logs and carries on: a throw here would take the whole route down
  // through its ErrorBoundary.
  const env = (import.meta as { env?: { DEV?: boolean } }).env;
  return env?.DEV === true;
}

function assertReconciles(condition: boolean, message: string): void {
  if (condition) return;
  const full = `revenue-matrix: ${message}`;
  if (isDevBuild()) throw new Error(full);
  console.error(full);
}

// ---------------------------------------------------------------------------
// Grouping
// ---------------------------------------------------------------------------

const MONTH =
  'january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sept|sep|oct|nov|dec';
const DAY = '\\d{1,2}(?:st|nd|rd|th)?';
const YEAR = '20\\d{2}';
/** A trailing day only counts when a year, a " - " separator or the end follows it. */
const DAY_AFTER_MONTH = `\\s+${DAY}(?=\\s+${YEAR}\\b|\\s+-\\s|\\s*$)`;
/**
 * A month word is only a date when a day or a 4-digit year sits next to it
 * ("Dec Ads package" and "May 2 sessions" keep their month):
 *   day[-day] <month> [day] [yyyy] | <month> day [yyyy] | <month> yyyy
 */
const DATE_STAMP = new RegExp(
  [
    `\\b${DAY}(?:\\s*-\\s*${DAY})?\\s+(?:${MONTH})\\b(?:${DAY_AFTER_MONTH})?(?:\\s+${YEAR}\\b)?`,
    `\\b(?:${MONTH})${DAY_AFTER_MONTH}(?:\\s+${YEAR}\\b)?`,
    `\\b(?:${MONTH})\\s+${YEAR}\\b`,
  ].join('|'),
  'g',
);
/** 20yy-yy, 20yy/yy (and 20yy-20yy) */
const YEAR_RANGE = /\b20\d{2}\s*[-/]\s*(?:20)?\d{2}\b/g;
/**
 * A bare 20yy is a year stamp only as a standalone token that ends the text
 * or a " - " segment, or that follows a season / period word ("Fall 2024
 * Participation Fee"). "Top 2000 Universities" keeps its number.
 */
const BARE_YEAR =
  /(?<![\w./-])20\d{2}(?![\w./-])(?:(?=\s*$|\s+-\s)|(?<=\b(?:fall|spring|summer|winter|autumn|intake|cycle|fy|year)\s+20\d{2}))/g;
const SEPARATORS = /\s*[;:,–—]\s*|\s+-\s+/g;
const DASH_RUN = /(?:\s*-\s*){2,}/g;
const PARENS = /\s*\(([^()]*)\)/g;
const EDGE_JUNK = /^[\s&-]+|[\s&-]+$/g;

function collapseSpaces(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * The uncatalogued-description grouping KEY (never shown). Owner rule from the
 * 2026-10-15 data study of the paid uncatalogued lines (0 wrong merges, 58 → 40
 * groups); this is the one place it lives:
 *   1. NFKC; cut everything from the first blank line.
 *   2. Collapse whitespace, trim, lower-case; "liason" → "liaison".
 *   3. ; : , – — and a spaced hyphen become " - "; runs of dashes collapse.
 *   4. "(x)" becomes " - x".
 *   5. Strip date stamps (a month word only with an adjacent day or year; see
 *      DATE_STAMP), 20yy-yy / 20yy/yy and bare year stamps (see BARE_YEAR);
 *      trim leftover " - " / "&" from both ends.
 * Plus one closing step the fixtures require ("India Spring" = "India - Spring"):
 * the remaining " - " separators become a plain space.
 * No fuzzy matching: "liason" is the only spelling fix.
 */
export function normaliseDescription(text: string): string {
  return normaliseKey(text, true);
}

/** Steps 1–5 of `normaliseDescription`; step 5's date stripping only when `stripDates`. */
function normaliseKey(text: string, stripDates: boolean): string {
  let key = text.normalize('NFKC');
  const blank = /\n\s*\n/.exec(key);
  if (blank) key = key.slice(0, blank.index);
  key = collapseSpaces(key).toLowerCase().replace(/liason/g, 'liaison');
  key = key.replace(SEPARATORS, ' - ').replace(DASH_RUN, ' - ');
  key = key.replace(PARENS, ' - $1');
  if (stripDates) {
    key = key.replace(YEAR_RANGE, ' ').replace(DATE_STAMP, ' ').replace(BARE_YEAR, ' ');
  }
  key = collapseSpaces(key).replace(DASH_RUN, ' - ').replace(EDGE_JUNK, '');
  key = key.replace(/\s+-\s+/g, ' ');
  return collapseSpaces(key);
}

const TRAILING_SEPARATOR = /(?:\s*[-–—:,;])+\s*$/;

/**
 * Display clean-up for a row label (never a key): collapse internal
 * whitespace and drop trailing separators ("Campaign -" → "Campaign").
 * Falls back to the collapsed text when nothing but separators is left.
 */
export function cleanLabel(text: string): string {
  const collapsed = collapseSpaces(text);
  const cleaned = collapsed.replace(TRAILING_SEPARATOR, '').trim();
  return cleaned !== '' ? cleaned : collapsed;
}

/** The first non-blank line of a raw description, trimmed ('' when none). */
function firstLine(text: string): string {
  for (const line of text.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (trimmed !== '') return trimmed;
  }
  return '';
}

function isCatalogued(line: MatrixLine): line is MatrixLine & { productId: string } {
  return !line.isUncatalogued && line.productId !== null && line.productId.trim() !== '';
}

export function groupKeyOf(line: MatrixLine): { key: string; label: string } {
  if (isCatalogued(line)) {
    const name = line.productName?.trim();
    const productId = line.productId.trim();
    return {
      key: productRowKey(productId),
      label: name ? name : `Product ${productId}`,
    };
  }
  // No product and not flagged uncatalogued: the header of an invoice that
  // has no lines. Not a Xero import, so it gets its own (catalogued) row.
  if (!line.isUncatalogued) return { key: NO_LINES_KEY, label: NO_LINES_LABEL };
  const itemCode = line.xeroItemCode?.trim();
  const raw = line.lineDescription ?? '';
  const label = firstLine(raw);
  if (itemCode) {
    return {
      key: `${UNCATALOGUED_PREFIX}item:${itemCode}`,
      label: label !== '' ? label : itemCode,
    };
  }
  if (label === '') return { key: NO_DESCRIPTION_KEY, label: NO_DESCRIPTION_LABEL };
  // A description that is nothing but a date stamp still needs a stable key:
  // the same steps without the date stripping ("May 2026" = "May 2026 - ").
  const normalised = normaliseDescription(raw);
  const fallback = normalised !== '' ? '' : normaliseKey(raw, false);
  const key =
    normalised !== ''
      ? normalised
      : fallback !== ''
        ? fallback
        : collapseSpaces(raw.normalize('NFKC')).toLowerCase();
  return {
    key: `${UNCATALOGUED_PREFIX}desc:${key}`,
    label,
  };
}

// ---------------------------------------------------------------------------
// Build
// ---------------------------------------------------------------------------

function compareCycles(a: MatrixCycle, b: MatrixCycle): number {
  if (a.start !== b.start) return a.start < b.start ? -1 : 1;
  return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
}

/** The fields ordering reads — a built row and a projected row both carry them. */
type SortableRow = Pick<MatrixRow, 'key' | 'label' | 'cells'> & { total: string | null };

function compareLabels(a: SortableRow, b: SortableRow): number {
  const byLabel = a.label.localeCompare(b.label, 'en', { sensitivity: 'base' });
  if (byLabel !== 0) return byLabel;
  return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
}

/** Chooses the columns: earliest revenue cycle → current (or latest revenue) cycle, by start date. */
function chooseColumns(
  revenueCycleIds: string[],
  allCycles: MatrixCycle[],
  currentCycleId: string | null,
): MatrixCycle[] {
  if (revenueCycleIds.length === 0) return [];
  const sorted = [...allCycles].sort(compareCycles);
  const positionOf = new Map<string, number>();
  sorted.forEach((cycle, index) => {
    if (!positionOf.has(cycle.id)) positionOf.set(cycle.id, index);
  });

  const knownPositions = revenueCycleIds
    .map((id) => positionOf.get(id))
    .filter((position): position is number => position !== undefined);

  const columns: MatrixCycle[] = [];
  if (knownPositions.length > 0) {
    const first = Math.min(...knownPositions);
    const lastRevenue = Math.max(...knownPositions);
    const currentPosition = currentCycleId === null ? undefined : positionOf.get(currentCycleId);
    // Never drop a cycle that has revenue, even one after "current".
    const last = currentPosition === undefined ? lastRevenue : Math.max(currentPosition, lastRevenue);
    columns.push(...sorted.slice(first, last + 1));
  }

  // Cycles in the lines but unknown to allCycles: no start date is known, so they go at the end.
  for (const id of revenueCycleIds) {
    if (!positionOf.has(id)) columns.push({ id, name: `Cycle ${id}`, start: '' });
  }
  return columns;
}

type RowAccumulator = {
  key: string;
  isUncatalogued: boolean;
  cells: (bigint | null)[];
  /** Row label → item count (most common wins). */
  labelCounts: Map<string, number>;
  labelOrder: string[];
  /** Uncatalogued product rows: raw description → summed value (highest value wins). */
  rawValue: Map<string, bigint>;
  rawOrder: string[];
  /** Whether any item supplied a `sublabel` key at all (absent = no sublabel on the row). */
  hasSublabel: boolean;
  /** Non-blank sublabel → item count (most common wins). */
  sublabelCounts: Map<string, number>;
  sublabelOrder: string[];
};

/** What one item contributes to its row in `buildRevenueMatrixBy`. */
export type MatrixRowSource = {
  /** Grouping key; items with the same key share a row. */
  key: string;
  /** Candidate label; the row shows the most common one (ties: first seen). */
  label: string;
  /** Candidate secondary label; the row shows the most common non-blank one, else null. */
  sublabel?: string | null;
  isUncatalogued?: boolean;
};

type CoreOptions<T> = {
  currentCycleId: string | null;
  cycleOf: (t: T) => string;
  amountOf: (t: T) => string;
  rowOf: (t: T) => MatrixRowSource;
  /**
   * Private hook for the product matrix: on an uncatalogued row the label is
   * the first line of the highest-value raw text instead of the most common label.
   */
  rawOf?: (t: T) => string | null;
};

/**
 * Generic core: any items × cycles. Same column, reconciliation, null/zero,
 * share, YoY and ordering rules as `buildRevenueMatrix`; the caller decides
 * the rows through `rowOf`.
 */
export function buildRevenueMatrixBy<T>(
  items: T[],
  allCycles: MatrixCycle[],
  opts: {
    currentCycleId: string | null;
    cycleOf: (t: T) => string;
    amountOf: (t: T) => string;
    rowOf: (t: T) => MatrixRowSource;
  },
): RevenueMatrix {
  return buildCore(items, allCycles, {
    currentCycleId: opts.currentCycleId,
    cycleOf: opts.cycleOf,
    amountOf: opts.amountOf,
    rowOf: opts.rowOf,
  });
}

export function buildRevenueMatrix(
  lines: MatrixLine[],
  allCycles: MatrixCycle[],
  opts: { currentCycleId: string | null },
): RevenueMatrix {
  return buildCore(lines, allCycles, {
    currentCycleId: opts.currentCycleId,
    cycleOf: (line) => line.cycleId,
    amountOf: (line) => line.amount,
    rowOf: (line) => {
      const { key, label } = groupKeyOf(line);
      return { key, label, isUncatalogued: key.startsWith(UNCATALOGUED_PREFIX) };
    },
    rawOf: (line) => line.lineDescription,
  });
}

/** One company's revenue line for the companies × cycles view of one product. */
export type CompanyMatrixLine = {
  cycleId: string;
  companyId: string;
  companyName: string;
  billingRegion: string | null;
  /** decimal string, already in the display currency */
  amount: string;
};

/** One product's revenue as companies (rows, keyed `company:<id>`) × cycles. */
export function buildProductCompanyMatrix(
  lines: CompanyMatrixLine[],
  allCycles: MatrixCycle[],
  opts: { currentCycleId: string | null },
): RevenueMatrix {
  return buildCore(lines, allCycles, {
    currentCycleId: opts.currentCycleId,
    cycleOf: (line) => line.cycleId,
    amountOf: (line) => line.amount,
    rowOf: (line) => {
      const name = line.companyName.trim();
      return {
        key: `company:${line.companyId}`,
        label: name !== '' ? name : `Company ${line.companyId}`,
        sublabel: line.billingRegion,
        isUncatalogued: false,
      };
    },
  });
}

function mostCommon(counts: Map<string, number>, order: string[]): string | null {
  let best: string | null = null;
  let bestCount = 0;
  for (const candidate of order) {
    const count = counts.get(candidate) ?? 0;
    if (count > bestCount) {
      bestCount = count;
      best = candidate;
    }
  }
  return best;
}

function buildCore<T>(items: T[], allCycles: MatrixCycle[], opts: CoreOptions<T>): RevenueMatrix {
  // Parse once; a non-decimal amount is a contract breach, not a zero.
  const parsed: { item: T; cycleId: string; value: Scaled }[] = [];
  for (const item of items) {
    const amount = opts.amountOf(item);
    const value = parseDecimal(amount);
    if (value === null) {
      assertReconciles(false, `line amount "${amount}" is not a decimal`);
      continue;
    }
    parsed.push({ item, cycleId: opts.cycleOf(item), value });
  }
  const scale = parsed.reduce((widest, entry) => Math.max(widest, entry.value.scale), MIN_SCALE);

  const revenueCycleIds: string[] = [];
  const seenCycle = new Set<string>();
  for (const { cycleId } of parsed) {
    if (!seenCycle.has(cycleId)) {
      seenCycle.add(cycleId);
      revenueCycleIds.push(cycleId);
    }
  }

  const cycles = chooseColumns(revenueCycleIds, allCycles, opts.currentCycleId);
  const columnOf = new Map<string, number>();
  cycles.forEach((cycle, index) => {
    if (!columnOf.has(cycle.id)) columnOf.set(cycle.id, index);
  });

  const rowsByKey = new Map<string, RowAccumulator>();
  const columnUnits: (bigint | null)[] = cycles.map(() => null);
  let lineSum = 0n;

  for (const { item, cycleId, value } of parsed) {
    const column = columnOf.get(cycleId);
    if (column === undefined) {
      assertReconciles(false, `cycle ${cycleId} has lines but no column`);
      continue;
    }
    const units = rescale(value, scale);
    lineSum += units;

    const source = opts.rowOf(item);
    const { key, label } = source;
    let row = rowsByKey.get(key);
    if (!row) {
      row = {
        key,
        isUncatalogued: source.isUncatalogued === true,
        cells: cycles.map(() => null),
        labelCounts: new Map(),
        labelOrder: [],
        rawValue: new Map(),
        rawOrder: [],
        hasSublabel: false,
        sublabelCounts: new Map(),
        sublabelOrder: [],
      };
      rowsByKey.set(key, row);
    }
    row.cells[column] = (row.cells[column] ?? 0n) + units;
    columnUnits[column] = (columnUnits[column] ?? 0n) + units;
    if (!row.labelCounts.has(label)) row.labelOrder.push(label);
    row.labelCounts.set(label, (row.labelCounts.get(label) ?? 0) + 1);
    if ('sublabel' in source && source.sublabel !== undefined) {
      row.hasSublabel = true;
      const sublabel = source.sublabel?.trim() ?? '';
      if (sublabel !== '') {
        if (!row.sublabelCounts.has(sublabel)) row.sublabelOrder.push(sublabel);
        row.sublabelCounts.set(sublabel, (row.sublabelCounts.get(sublabel) ?? 0) + 1);
      }
    }
    if (opts.rawOf) {
      const raw = (opts.rawOf(item) ?? '').trim();
      if (raw !== '') {
        if (!row.rawValue.has(raw)) row.rawOrder.push(raw);
        row.rawValue.set(raw, (row.rawValue.get(raw) ?? 0n) + units);
      }
    }
  }

  const grandUnits = columnUnits.reduce<bigint>((sum, units) => sum + (units ?? 0n), 0n);
  let rowTotalSum = 0n;
  let maxUnits: bigint | null = null;

  const rows: MatrixRow[] = [];
  for (const row of rowsByKey.values()) {
    // Owner rule: drop a row only when every cell is null or exactly zero.
    // A row with non-zero cells that net to a zero total stays.
    if (row.cells.every((cell) => cell === null || cell === 0n)) continue;

    // Catalogued: most common product name. Uncatalogued: first non-blank
    // line of the highest-value raw description. Ties go to the first seen.
    let label = row.labelOrder[0] ?? '';
    if (row.isUncatalogued && row.rawOrder.length > 0) {
      let bestRaw = row.rawOrder[0] ?? '';
      let bestValue = row.rawValue.get(bestRaw) ?? 0n;
      for (const candidate of row.rawOrder) {
        const value = row.rawValue.get(candidate) ?? 0n;
        if (value > bestValue) {
          bestValue = value;
          bestRaw = candidate;
        }
      }
      const line = firstLine(bestRaw);
      if (line !== '') label = line;
    } else {
      label = mostCommon(row.labelCounts, row.labelOrder) ?? label;
    }

    let totalUnits = 0n;
    for (const cell of row.cells) {
      if (cell === null) continue;
      totalUnits += cell;
      if (maxUnits === null || cell > maxUnits) maxUnits = cell;
    }
    rowTotalSum += totalUnits;

    const built: MatrixRow = {
      key: row.key,
      label: cleanLabel(label),
      isUncatalogued: row.isUncatalogued,
      cells: row.cells.map((cell) => (cell === null ? null : formatUnits(cell, scale))),
      total: formatUnits(totalUnits, scale),
      share: grandUnits <= 0n ? null : formatTenths(divideRoundHalf(totalUnits * 1000n, grandUnits), false),
    };
    // Only rows whose items carry a sublabel get the key (product rows stay unchanged).
    if (row.hasSublabel) built.sublabel = mostCommon(row.sublabelCounts, row.sublabelOrder);
    rows.push(built);
  }

  assertReconciles(rowTotalSum === grandUnits, 'row totals do not sum to the grand total');
  assertReconciles(lineSum === grandUnits, 'line amounts do not sum to the grand total');
  cycles.forEach((_, column) => {
    // Across every group, kept or dropped: a dropped row's zero cells still
    // count as "a line in this cycle" for the column total.
    const fromRows = [...rowsByKey.values()].reduce<bigint | null>((sum, row) => {
      const cell = row.cells[column] ?? null;
      return cell === null ? sum : (sum ?? 0n) + cell;
    }, null);
    assertReconciles(fromRows === (columnUnits[column] ?? null), `column ${column} does not reconcile`);
  });

  const yoy = columnUnits.map((current, index) => {
    if (index === 0) return null;
    const previous = columnUnits[index - 1] ?? null;
    if (previous === null || previous === 0n || current === null) return null;
    const magnitude = previous < 0n ? -previous : previous;
    return formatTenths(divideRoundHalf((current - previous) * 1000n, magnitude), true);
  });

  const rowsSorted = sortRows(rows, { by: 'total', dir: 'desc' });

  return {
    cycles,
    rows: rowsSorted,
    columnTotals: columnUnits.map((units) => (units === null ? null : formatUnits(units, scale))),
    grandTotal: formatUnits(grandUnits, scale),
    yoy,
    maxCell: maxUnits === null ? null : formatUnits(maxUnits, scale),
  };
}

// ---------------------------------------------------------------------------
// Sorting, heat, CSV
// ---------------------------------------------------------------------------

/** Compares two decimal strings exactly; null sorts as "missing" (handled by the caller). */
function compareDecimal(a: string, b: string): number {
  const pa = parseDecimal(a);
  const pb = parseDecimal(b);
  if (pa === null || pb === null) return pa === pb ? 0 : pa === null ? 1 : -1;
  const scale = Math.max(pa.scale, pb.scale);
  const ua = rescale(pa, scale);
  const ub = rescale(pb, scale);
  return ua < ub ? -1 : ua > ub ? 1 : 0;
}

/**
 * Returns a new, stably sorted array. Label sort is case-insensitive. For a
 * cycle column, empty (null) cells always sort last regardless of direction;
 * so does an absent row total (a projected row whose visible cells are all
 * empty). Ties fall back to label ascending, then key.
 */
export function sortRows<T extends SortableRow>(rows: readonly T[], sort: MatrixSort): T[] {
  const direction = sort.dir === 'asc' ? 1 : -1;
  const by = sort.by;
  const nullsLast = (x: string | null, y: string | null): number | null => {
    if (x !== null && y !== null) return null;
    return x === y ? 0 : x === null ? 1 : -1;
  };
  return [...rows].sort((a, b) => {
    if (by === 'label') {
      return direction * compareLabels(a, b);
    }
    const ca = by === 'total' ? a.total : (a.cells[by.cycleIndex] ?? null);
    const cb = by === 'total' ? b.total : (b.cells[by.cycleIndex] ?? null);
    const primary = nullsLast(ca, cb) ?? direction * compareDecimal(ca as string, cb as string);
    return primary !== 0 ? primary : compareLabels(a, b);
  });
}

/**
 * The matrix as the reader chose to see it: only the visible cycles and rows,
 * with every derived figure recomputed over exactly those — owner, 2026-10-27.
 *
 * - **Row total** = the sum of its VISIBLE cells; `null` when every visible
 *   cell is empty (the row is KEPT and renders a dash — only the reader hides a
 *   row).
 * - **Column totals, grand total, share** — over visible rows × visible cycles,
 *   so the visible shares add up to the visible grand total.
 * - **YoY** compares a visible column with the cycle immediately BEFORE it in
 *   the full list, hidden or not, both summed over the visible rows. Comparing
 *   with the previous VISIBLE column would let the label "change on previous
 *   cycle" describe a jump of several years.
 * - **maxCell** — over visible cells, so the heat scale follows what is shown.
 *
 * Exact BigInt maths at the widest scale any cell carries, like the builder.
 * Nothing hidden returns the matrix itself, byte for byte.
 */
export function projectMatrix(
  m: RevenueMatrix,
  isCycleVisible: (cycle: MatrixCycle) => boolean,
  isRowVisible: (row: MatrixRow) => boolean,
): VisibleMatrix {
  const cycleShown = m.cycles.map((cycle) => isCycleVisible(cycle));
  const rows = m.rows.filter((row) => isRowVisible(row));
  if (cycleShown.every(Boolean) && rows.length === m.rows.length) return m;

  let scale = MIN_SCALE;
  for (const row of m.rows) {
    for (const cell of row.cells) {
      const parsed = cell === null ? null : parseDecimal(cell);
      if (parsed !== null) scale = Math.max(scale, parsed.scale);
    }
  }
  const unitsOf = (cell: string | null): bigint | null => {
    const parsed = cell === null ? null : parseDecimal(cell);
    return parsed === null ? null : rescale(parsed, scale);
  };

  // Every ORIGINAL column summed over the visible rows (YoY needs hidden ones too).
  const fullColumnUnits: (bigint | null)[] = m.cycles.map(() => null);
  const rowUnits = rows.map((row) => row.cells.map(unitsOf));
  for (const cells of rowUnits) {
    cells.forEach((units, column) => {
      if (units !== null) fullColumnUnits[column] = (fullColumnUnits[column] ?? 0n) + units;
    });
  }

  const visibleIndexes = cycleShown.flatMap((shown, index) => (shown ? [index] : []));
  let grandUnits: bigint | null = null;
  for (const index of visibleIndexes) {
    const units = fullColumnUnits[index] ?? null;
    if (units !== null) grandUnits = (grandUnits ?? 0n) + units;
  }

  let maxUnits: bigint | null = null;
  const projectedRows: VisibleMatrixRow[] = rows.map((row, r) => {
    let total: bigint | null = null;
    const cells = visibleIndexes.map((index) => {
      const units = rowUnits[r]?.[index] ?? null;
      if (units !== null) {
        total = (total ?? 0n) + units;
        if (maxUnits === null || units > maxUnits) maxUnits = units;
      }
      return row.cells[index] ?? null;
    });
    const rowTotal: bigint | null = total;
    return {
      ...row,
      cells,
      total: rowTotal === null ? null : formatUnits(rowTotal, scale),
      share:
        rowTotal === null || grandUnits === null || grandUnits <= 0n
          ? null
          : formatTenths(divideRoundHalf(rowTotal * 1000n, grandUnits), false),
    };
  });

  const yoy = visibleIndexes.map((index) => {
    if (index === 0) return null;
    const current = fullColumnUnits[index] ?? null;
    const previous = fullColumnUnits[index - 1] ?? null;
    if (previous === null || previous === 0n || current === null) return null;
    const magnitude = previous < 0n ? -previous : previous;
    return formatTenths(divideRoundHalf((current - previous) * 1000n, magnitude), true);
  });

  const visibleMax: bigint | null = maxUnits;
  return {
    cycles: visibleIndexes.map((index) => m.cycles[index] as MatrixCycle),
    rows: sortRows(projectedRows, { by: 'total', dir: 'desc' }),
    columnTotals: visibleIndexes.map((index) => {
      const units = fullColumnUnits[index] ?? null;
      return units === null ? null : formatUnits(units, scale);
    }),
    grandTotal: grandUnits === null ? null : formatUnits(grandUnits, scale),
    yoy,
    maxCell: visibleMax === null ? null : formatUnits(visibleMax, scale),
  };
}

/** 0 for null / zero / negative / no positive max; else ceil(5 × value / max) clamped to 1..5. */
export function heatLevel(value: string | null, max: string | null): 0 | 1 | 2 | 3 | 4 | 5 {
  if (value === null || max === null) return 0;
  const pv = parseDecimal(value);
  const pm = parseDecimal(max);
  if (pv === null || pm === null) return 0;
  const scale = Math.max(pv.scale, pm.scale);
  const v = rescale(pv, scale);
  const m = rescale(pm, scale);
  if (v <= 0n || m <= 0n) return 0;
  const level = (5n * v + m - 1n) / m; // ceil for positive operands
  if (level <= 1n) return 1;
  if (level >= 5n) return 5;
  return Number(level) as 2 | 3 | 4;
}

/** RFC 4180 field quoting. */
function csvField(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** Labels come from Xero line text: neutralise spreadsheet formula injection. */
function defuseFormula(value: string): string {
  return /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
}

/**
 * The matrix as a CSV table only (no title line), CRLF line endings, no BOM.
 * The third argument (company or product name) is accepted for the caller's
 * file naming contract and is not written into the table. `rowHeader` heads
 * the label column ("Company" for the product view).
 */
export function matrixToCsv(
  /** Pass the `projectMatrix` result: the file then holds only what is visible. */
  m: VisibleMatrix,
  currencyCode: string,
  _name: string,
  rowHeader = 'Product',
): string {
  const code = currencyCode.trim().toUpperCase();
  const header = [
    rowHeader,
    ...m.cycles.map((cycle) => `${defuseFormula(cycle.name)} (${code})`),
    `Total (${code})`,
    'Share %',
  ];
  const body = m.rows.map((row) => [
    defuseFormula(row.label),
    ...row.cells.map((cell) => cell ?? ''),
    row.total ?? '',
    row.share ?? '',
  ]);
  const grandPositive = m.grandTotal !== null && compareDecimal(m.grandTotal, '0') > 0;
  const totalRow = [
    'Total',
    ...m.columnTotals.map((total) => total ?? ''),
    m.grandTotal ?? '',
    grandPositive ? '100.0' : '',
  ];
  const yoyRow = ['YoY change', ...m.yoy.map((change) => (change === null ? '' : `${change}%`)), '', ''];
  return [header, ...body, totalRow, yoyRow]
    .map((fields) => fields.map(csvField).join(','))
    .join('\r\n');
}
